import { createMRTColumnHelper, MRT_ColumnDef } from "material-react-table";
import { Person, PersonNameInternalEvidence } from "../../types/person";
import {
  EntityWithMetadataTableSuffixColumns,
  createEntityFilterSpec,
  publishedIdColumn,
  withFieldProofreadingIndicator,
} from "./EntityUtils";
import type { FilterSpec } from "../../hooks/useServerFilters";
import { GetIntellexusDate } from "../../components/forms/common/intellexusDate/IntellexusDateField";
import { IntellexusDateStructure } from "../../types/intellexusDate";
import {
  formatLinksList,
  LinksList,
} from "../../components/forms/common/subForms/LinksListField";
import {
  formatLanguagesList,
  LanguagesList,
} from "../../components/forms/common/subForms/LanguagesListField";
import { Box } from "@mui/material";
import { stringifyPersonName } from "../../hooks/logic/personLogic";
import EntityReferenceComponent from "../../components/entitiesReferences/EntityReferenceComponent";

const columnHelper = createMRTColumnHelper<Person>();

function normalizeText(input: string | undefined | null): string {
  return (input ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function personNameMatchesFilter(
  evidence: PersonNameInternalEvidence | null | undefined,
  filterValue: string | null | undefined
): boolean {
  if (!evidence || !filterValue) return false;

  const { attribute_pre = "", name = "", attribute_post = "" } = evidence;

  const fullName = normalizeText([attribute_pre, name, attribute_post].filter(Boolean).join(" "));
  const query = normalizeText(filterValue);

  return fullName.includes(query);
}

const createPersonColumns = (
  withProofreading: boolean = false
): MRT_ColumnDef<Person, any>[] => [
  publishedIdColumn,
  columnHelper.accessor("name_internal_evidence", {
    header: "Name",
    Cell: withFieldProofreadingIndicator(
      "name_internal_evidence",
      ({ cell }) => {
        const evidence = cell.getValue() as PersonNameInternalEvidence;
        return stringifyPersonName(evidence) || "-";
      },
      withProofreading
    ),
    filterFn: (row, _id, filterValue) =>
      personNameMatchesFilter(row.original.name_internal_evidence, filterValue),
  }),
  columnHelper.accessor("name_internal_evidence.reconstruction_status_type", {
    header: "Name reconstruction status type",
    Cell: withFieldProofreadingIndicator(
      "name_internal_evidence",
      ({ cell }) => cell.getValue() || "-",
      withProofreading
    ),
  }),
  columnHelper.accessor("name_internal_evidence.status_type", {
    header: "Name status type",
    Cell: withFieldProofreadingIndicator(
      "name_internal_evidence",
      ({ cell }) => cell.getValue() || "-",
      withProofreading
    ),
  }),
  columnHelper.accessor("origin", {
    header: "Origin",
    Cell: ({ cell }) => cell.getValue() || "-",
  }),
  columnHelper.accessor("gender", {
    header: "Gender",
    Cell: ({ cell }) => cell.getValue() || "-",
  }),
  columnHelper.accessor("place_of_birth", {
    header: "Place of Birth",
    enableColumnFilter: false,
    Cell: ({ cell }) => <EntityReferenceComponent entity={cell.getValue()} />,
  }),
  columnHelper.accessor("date_of_birth", {
    header: "Date of Birth",
    filterVariant: "text",
    Cell: withFieldProofreadingIndicator("date_of_birth", ({ cell }) => {
      if (!cell.getValue()) return "-";
      const intellexusDate = cell.getValue() as IntellexusDateStructure;
      return GetIntellexusDate(intellexusDate);
    }, withProofreading),
  }),
  columnHelper.accessor("floruit", {
    header: "Floruit",
    Cell: withFieldProofreadingIndicator("floruit", ({ cell }) => {
      if (!cell.getValue()) return "-";
      const intellexusDate = cell.getValue() as IntellexusDateStructure;
      return GetIntellexusDate(intellexusDate);
    }, withProofreading),
  }),
  columnHelper.accessor("date_of_death", {
    header: "Date of death",
    Cell: withFieldProofreadingIndicator("date_of_death", ({ cell }) => {
      if (!cell.getValue()) return "-";
      const intellexusDate = cell.getValue() as IntellexusDateStructure;
      return GetIntellexusDate(intellexusDate);
    }, withProofreading),
  }),
  columnHelper.accessor("region_of_activity", {
    header: "Region of Activity",
    enableColumnFilter: false,
    Cell: withFieldProofreadingIndicator(
      "region_of_activity",
      ({ cell }) => <EntityReferenceComponent entity={cell.getValue()} />,
      withProofreading
    ),
  }),
  columnHelper.accessor("other_designations", {
    header: "Designations",
    Cell: withFieldProofreadingIndicator("other_designations", ({ cell }) => {
      const input = cell.getValue() as PersonNameInternalEvidence[];
      if (!input || input.length === 0) return "-";

      return (
        <Box>
          {input.map((evidence: PersonNameInternalEvidence, index: number) => (
            <Box key={index} sx={{ whiteSpace: "nowrap" }}>
              {stringifyPersonName(evidence)}
            </Box>
          ))}
        </Box>
      );
    }, withProofreading),
  }),
  columnHelper.accessor("primary_title_type", {
    header: "Primary title type",
    Cell: withFieldProofreadingIndicator(
      "primary_title_type",
      ({ cell }) => cell.getValue() || "-",
      withProofreading
    ),
  }),
  columnHelper.accessor("link_to_other_databases", {
    header: "Links",
    Cell: withFieldProofreadingIndicator(
      "link_to_other_databases",
      ({ cell }) => {
        const input = cell.getValue() as LinksList;
        return formatLinksList(input);
      },
      withProofreading
    ),
  }),
  columnHelper.accessor("languages", {
    header: "Languages",
    Cell: ({ cell }) => {
      const input = cell.getValue() as LanguagesList;
      return formatLanguagesList(input);
    },
  }),
  columnHelper.accessor("affiliated_traditions", {
    header: "Affiliated traditions",
    Cell: withFieldProofreadingIndicator(
      "affiliated_traditions",
      ({ cell }) => cell.getValue() || "-",
      withProofreading
    ),
  }),
  ...EntityWithMetadataTableSuffixColumns,
];

export const personColumns = createPersonColumns();

export const personTableColumns = createPersonColumns(true);

export const personFilterSpec: FilterSpec = createEntityFilterSpec({
  name_internal_evidence: { kind: "string" },
  "name_internal_evidence.reconstruction_status_type": { kind: "string" },
  "name_internal_evidence.status_type": { kind: "string" },
  origin: { kind: "string" },
  gender: { kind: "string" },
  date_of_birth: { kind: "string" },
  floruit: { kind: "string" },
  date_of_death: { kind: "string" },
  other_designations: { kind: "string" },
  primary_title_type: { kind: "string" },
  link_to_other_databases: { kind: "string" },
  languages: { kind: "string" },
  affiliated_traditions: { kind: "string" },
});

export default personColumns;
